"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Phone,
  Mail,
  MapPin,
  Instagram,
  Send,
} from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { business, telHref, mailHref, instagramHref, telegramHref } from "@/content/business";
import { serviceOptions } from "@/content/services";

type ContactFormData = {
  name: string;
  phone: string;
  service: (typeof serviceOptions)[number];
  location?: string;
  message?: string;
};

type ContactFormState = {
  name: string;
  phone: string;
  service: ContactFormData["service"];
  location: string;
  message: string;
  website: string;
};

type SubmissionStatus = "idle" | "submitting" | "success" | "error";

const initialFormState: ContactFormState = {
  name: "",
  phone: "",
  service: serviceOptions[0],
  location: "",
  message: "",
  website: "",
};

function validateClient(
  data: ContactFormState,
): { success: true; data: ContactFormData } | { success: false; errors: Partial<Record<keyof ContactFormData, string>> } {
  const errors: Partial<Record<keyof ContactFormData, string>> = {};

  const name = data.name.trim();
  if (!name) errors.name = "Укажите имя";
  else if (name.length < 2) errors.name = "Имя должно содержать минимум 2 символа";
  else if (name.length > 100) errors.name = "Имя слишком длинное";

  const phone = data.phone.trim();
  if (!phone) errors.phone = "Укажите номер телефона";
  else if (phone.length < 5) errors.phone = "Введите номер телефона";
  else if (phone.length > 20) errors.phone = "Номер слишком длинный";
  else if (!/^[+\d\s()-]{5,20}$/u.test(phone)) errors.phone = "Используйте только цифры, + и дефисы";

  const service = data.service;
  if (!(serviceOptions as readonly string[]).includes(service)) errors.service = "Выберите услугу";

  const location = data.location.trim();
  if (location.length > 120) errors.location = "Название слишком длинное";

  const message = data.message.trim();
  if (message.length > 1000) errors.message = "Сообщение слишком длинное";

  if (Object.keys(errors).length > 0) return { success: false, errors };

  return {
    success: true,
    data: {
      name,
      phone,
      service,
      location: location || undefined,
      message: message || undefined,
    },
  };
}

export function ContactSection() {
  const [formData, setFormData] = useState<ContactFormState>(initialFormState);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [status, setStatus] = useState<SubmissionStatus>("idle");
  const [feedback, setFeedback] = useState<string | null>(null);
  const sectionRef = useRevealOnScroll<HTMLElement>({
    threshold: 0.1,
    staggerInterval: 200,
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFeedback(null);
    setStatus("idle");

    if (formData.website.trim()) {
      setStatus("success");
      setFeedback("Заявка отправлена. Мы свяжемся с вами для подтверждения и уточнения деталей.");
      setFormData(initialFormState);
      return;
    }

    const parsed = validateClient(formData);

    if (!parsed.success) {
      setFieldErrors(parsed.errors);
      setStatus("error");
      setFeedback("Проверьте выделенные поля и попробуйте снова.");
      return;
    }

    setFieldErrors({});
    setStatus("submitting");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...parsed.data, website: formData.website }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        if (errorBody?.errors) {
          setFieldErrors({
            name: errorBody.errors.name?.[0],
            phone: errorBody.errors.phone?.[0],
            service: errorBody.errors.service?.[0],
            location: errorBody.errors.location?.[0],
            message: errorBody.errors.message?.[0],
          });
        }
        throw new Error(errorBody?.message ?? "Сервер временно недоступен");
      }

      const result = await response.json().catch(() => ({}));
      setStatus("success");
      setFeedback((result as { message?: string }).message ?? "Спасибо! Мы получили вашу заявку.");
      setFormData({
        name: "",
        phone: "",
        service: parsed.data.service,
        location: "",
        message: "",
        website: "",
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Неизвестная ошибка.";
      setStatus("error");
      setFeedback(`${message} Вы всегда можете позвонить нам напрямую.`);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name !== "website") {
      setFieldErrors((prev) => ({ ...prev, [name as keyof ContactFormData]: undefined }));
    }
  };

  return (
    <section ref={sectionRef} id="contact" className="bg-luxury-dark py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="grid gap-16 lg:grid-cols-2">
          <div className="reveal">
            <div className="text-luxury-green mb-6 inline-flex items-center space-x-3">
              <div className="bg-luxury-green h-0.5 w-12" />
              <span className="text-sm font-semibold tracking-[0.3em] uppercase">Свяжитесь с нами</span>
            </div>
            <h2 className="font-display mb-6 text-5xl font-bold text-white lg:text-6xl">Начните свой проект</h2>
            <p className="mb-12 text-xl text-white/70">
              Позвоните нам для бесплатного замера и расчёта стоимости. {business.phone.manager} ответит на все ваши вопросы.
            </p>
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="bg-luxury-green/20 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full">
                  <Phone className="text-luxury-green h-5 w-5" />
                </div>
                <div>
                  <p className="mb-2 text-sm tracking-wider text-white/60 uppercase">Основной номер</p>
                  <Link href={telHref} className="font-display hover:text-luxury-green mb-2 block text-2xl text-white transition-colors">
                    {business.phone.display}
                  </Link>
                  <p className="text-sm text-white/50">Менеджер: {business.phone.manager}</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-luxury-green/20 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full">
                  <Mail className="text-luxury-green h-5 w-5" />
                </div>
                <div>
                  <p className="mb-2 text-sm tracking-wider text-white/60 uppercase">Электронная почта</p>
                  <Link href={mailHref} className="font-display hover:text-luxury-green text-2xl text-white transition-colors">
                    {business.email}
                  </Link>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-luxury-green/20 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full">
                  <MapPin className="text-luxury-green h-5 w-5" />
                </div>
                <div>
                  <p className="mb-2 text-sm tracking-wider text-white/60 uppercase">Регион работы</p>
                  <p className="font-display text-2xl text-white">{business.address.region}</p>
                  <p className="mt-1 text-sm text-white/50">Работаем по всей территории ЧР</p>
                </div>
              </div>
            </div>
            <div className="mt-12 flex space-x-4">
              <Link href={instagramHref} target="_blank" rel="noopener noreferrer" className="border-luxury-green/30 text-luxury-green hover:bg-luxury-green flex h-12 w-12 items-center justify-center rounded-full border transition-all duration-300 hover:text-white">
                <Instagram className="h-5 w-5" />
              </Link>
              <Link href={telegramHref} target="_blank" rel="noopener noreferrer" aria-label="Telegram" className="border-luxury-green/30 text-luxury-green hover:bg-luxury-green flex h-12 w-12 items-center justify-center rounded-full border transition-all duration-300 hover:text-white">
                <Send className="h-5 w-5" />
              </Link>
            </div>
          </div>

          <div className="reveal bg-white p-10 lg:p-12">
            <h3 className="font-display text-luxury-dark mb-8 text-3xl font-bold">Заказать бесплатный замер</h3>
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div className="hidden" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input type="text" id="website" name="website" value={formData.website} onChange={handleChange} autoComplete="off" tabIndex={-1} />
              </div>

              <div>
                <label htmlFor="contact-name" className="text-luxury-stone mb-3 block text-sm font-semibold tracking-wider uppercase">Ваше имя</label>
                <input type="text" id="contact-name" name="name" value={formData.name} onChange={handleChange} required aria-invalid={Boolean(fieldErrors.name)} aria-describedby={fieldErrors.name ? "contact-name-error" : undefined} className={`w-full border px-4 py-3 transition-colors ${fieldErrors.name ? "border-red-500 focus:border-red-500" : "border-luxury-stone/20 focus:border-luxury-green"} focus:outline-none`} />
                {fieldErrors.name && <p id="contact-name-error" className="mt-2 text-sm text-red-500">{fieldErrors.name}</p>}
              </div>

              <div>
                <label htmlFor="contact-phone" className="text-luxury-stone mb-3 block text-sm font-semibold tracking-wider uppercase">Номер телефона</label>
                <input type="tel" id="contact-phone" name="phone" value={formData.phone} onChange={handleChange} required aria-invalid={Boolean(fieldErrors.phone)} aria-describedby={fieldErrors.phone ? "contact-phone-error" : undefined} className={`w-full border px-4 py-3 transition-colors ${fieldErrors.phone ? "border-red-500 focus:border-red-500" : "border-luxury-stone/20 focus:border-luxury-green"} focus:outline-none`} />
                {fieldErrors.phone && <p id="contact-phone-error" className="mt-2 text-sm text-red-500">{fieldErrors.phone}</p>}
              </div>

              <div>
                <label htmlFor="contact-service" className="text-luxury-stone mb-3 block text-sm font-semibold tracking-wider uppercase">Тип услуги</label>
                <select id="contact-service" name="service" value={formData.service} onChange={handleChange} aria-invalid={Boolean(fieldErrors.service)} aria-describedby={fieldErrors.service ? "contact-service-error" : undefined} className={`w-full border px-4 py-3 transition-colors ${fieldErrors.service ? "border-red-500 focus:border-red-500" : "border-luxury-stone/20 focus:border-luxury-green"} focus:outline-none`}>
                  {serviceOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                {fieldErrors.service && <p id="contact-service-error" className="mt-2 text-sm text-red-500">{fieldErrors.service}</p>}
              </div>

              <div>
                <label htmlFor="contact-location" className="text-luxury-stone mb-3 block text-sm font-semibold tracking-wider uppercase">Город/район</label>
                <input type="text" id="contact-location" name="location" value={formData.location} onChange={handleChange} placeholder="Например: Грозный" aria-invalid={Boolean(fieldErrors.location)} aria-describedby={fieldErrors.location ? "contact-location-error" : undefined} className={`w-full border px-4 py-3 transition-colors ${fieldErrors.location ? "border-red-500 focus:border-red-500" : "border-luxury-stone/20 focus:border-luxury-green"} focus:outline-none`} />
                {fieldErrors.location && <p id="contact-location-error" className="mt-2 text-sm text-red-500">{fieldErrors.location}</p>}
              </div>

              <div>
                <label htmlFor="contact-message" className="text-luxury-stone mb-3 block text-sm font-semibold tracking-wider uppercase">Дополнительная информация</label>
                <textarea id="contact-message" name="message" value={formData.message} onChange={handleChange} rows={4} placeholder="Площадь, сроки, особые требования..." aria-invalid={Boolean(fieldErrors.message)} aria-describedby={fieldErrors.message ? "contact-message-error" : undefined} className={`w-full resize-none border px-4 py-3 transition-colors ${fieldErrors.message ? "border-red-500 focus:border-red-500" : "border-luxury-stone/20 focus:border-luxury-green"} focus:outline-none`} />
                {fieldErrors.message && <p id="contact-message-error" className="mt-2 text-sm text-red-500">{fieldErrors.message}</p>}
              </div>

              <button type="submit" disabled={status === "submitting"} className={`gradient-luxury group flex w-full items-center justify-center space-x-3 py-4 font-semibold tracking-wider text-white transition-all duration-300 ${status === "submitting" ? "cursor-not-allowed opacity-80" : "hover:shadow-xl"}`}>
                <span>{status === "submitting" ? "ОТПРАВКА..." : "ОТПРАВИТЬ ЗАЯВКУ"}</span>
                <Send className="h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>

              <div aria-live="polite" className="min-h-[1.5rem] text-center">
                {feedback && <p className={`text-sm ${status === "success" ? "text-luxury-green" : "text-red-500"}`}>{feedback}</p>}
              </div>

              <p className="text-luxury-stone text-center text-xs">
                Нажимая кнопку, вы соглашаетесь с <Link href="/privacy-policy" className="hover:text-luxury-green underline">политикой конфиденциальности</Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
